// Where an app is in its life, for the lifecycle rail. Pure: the three
// stages and the Lab phases live in consoleCatalog (so NoVA and the rail
// share the words); this file decides "here" vs "reached", which later
// stages are not ready yet (and why), and the ONE next step. Reached is per
// stage and sticky — a live app can sit on the Lab without clearing
// Operate.
//
// `nextStep(app, facts)` is the single source for: the rail's check marks
// and disabled states, the stage brief, the "Next" button, NoVA's
// studioText, and Operate's suggestion ordering (decision lab-is-the-studio).
//
// generate-entities.mjs copies this file to frontend/src/data/lifecycle.js.
import { LIFECYCLE, LIFECYCLE_FEEDS, LAB_PHASES, STUDIO_JOURNEY } from './consoleCatalog.js';

export { LIFECYCLE, LIFECYCLE_FEEDS, LAB_PHASES };

// Journey steps that mean the Lab's Think phases have actually been used.
// Interview alone has not produced a concept or a run.
const LAB_STEPS = new Set(['research', 'concept', 'review', 'stories', 'build', 'test', 'done']);
// Journey steps where the studio conversation is still shaping the idea.
const THINKING = new Set(['interview', 'research', 'concept', 'review']);

const defined = (v) => v !== undefined;

// Signals from the records App Builder already has, or from the operate
// feed a library page loads. Omitted fields fall through to `operate`;
// an explicit null means "looked, and it is absent". The phase-level keys
// (design / surfaces / try) feed the Lab rail; the stage keys feed the
// lifecycle rail.
export function signalsFrom({
  app = null,
  org = null,
  operate = null,
  agents,
  liveDocs,
  instance,
  integrations,
  lastRun,
  release,
  lab,
  build,
} = {}) {
  const op = operate || {};
  const agentList = defined(agents) ? agents : (defined(op.agents) ? op.agents : null);
  const agentCount = agentList ? agentList.length : (app?.links?.agentIds?.length || 0);
  const origins = (agentList || []).some((a) => (a.allowedOrigins || []).length > 0);
  const docs = defined(liveDocs) ? liveDocs : (op.docs ? op.docs.filter((d) => d.status === 'live') : null);
  const liveCount = docs ? docs.length : 0;
  const inst = defined(instance) ? instance : (op.instance || null);
  const linked = !!(inst || app?.links?.instanceAppId);
  const rel = defined(release) ? release : ((op.releases || [])[0] || null);
  const run = defined(lastRun) ? lastRun : ((op.qa || [])[0] || null);
  const labData = defined(lab) ? lab : (op.lab || null);
  const guards = org?.guardrails;
  // The workflow gate: linked AND its design requirements met. With no
  // requirements declared (or no operate feed) the link alone still counts,
  // so apps from before requirements existed keep their gate.
  const pr = op.data?.pipelineRequirements || null;
  const pipeline = !!(app?.links?.pipelineId || op.data?.pipelineId) && !(pr && pr.total > 0 && pr.unmet > 0);
  const integrationsOk = Array.isArray(integrations) && integrations.length > 0 && integrations.every((i) => i.lastTest?.ok);
  const bld = defined(build) ? build : (op.build || inst?.monitor?.build || app?.build || null);
  const gates = agentCount > 0
    && liveCount > 0
    && !!(guards && Object.values(guards).some(Boolean))
    && pipeline
    && integrationsOk
    && run?.status === 'passed'
    && linked;
  const journey = app?.journey?.step;
  const thought = !!(
    labData?.concept?.accepted
    || labData?.concept?.latest
    || (labData?.runs > 0)
    || (labData?.stories?.total > 0)
    || (journey && LAB_STEPS.has(journey))
  );
  const design = !!(app?.name && app?.goal && agentCount > 0);
  return {
    // Lab phases
    design,
    surfaces: !!(linked || origins),
    try: agentCount > 0,
    think: thought,
    // Stages
    lab: design || thought,
    ship: gates && ['approved', 'released'].includes(rel?.status),
    operate: inst?.status === 'live',
    // Facts nextStep reads
    agentCount, liveCount, linked, instance: inst, release: rel, run, gates, pipelineRequirements: pr,
    concept: labData?.concept || null, stories: labData?.stories || null, journey: journey || null,
    build: bld, sync: app?.links?.instanceSync || op.sync || null,
    // The design is behind the build (appOperate devActivity): Dev Console
    // sessions + NoVA turns since the last catch-up.
    behind: op.dev?.behind === true,
    dev: op.dev || null,
  };
}

// Which later stages are not ready yet, and the one sentence that says
// why. Guided gates: the stage stays visible, disabled, with the blocker as
// its tooltip. The Lab is never blocked.
export function stageGates(s = {}) {
  const purposed = !!(s.concept?.accepted || s.design || s.agentCount > 0);
  return {
    lab: null,
    ship: purposed ? null : 'Say what the app is in Lab › Design, or accept a concept, first',
    operate: s.linked ? null : 'Link or launch a hosted instance in Lab › Surfaces first',
  };
}

// The ONE next step: { stage, phase, label, text, blocker, href? }. Reads
// `signalsFrom` output (or just an app record when that is all there is —
// NoVA's studioText). Order is the order a person would do it; the first
// open item wins. `label` fits a button; `text` is the sentence under it.
export function nextStep({ app = null, signals = null } = {}) {
  const s = signals || signalsFrom({ app });
  const j = s.journey || app?.journey?.step || null;
  const step = (stage, phase, label, text, extra = {}) => ({ stage, phase, label, text, blocker: null, ...extra });
  if (!app?.name || !app?.goal) {
    if (j && THINKING.has(j)) return step('lab', 'concept', 'Finish the concept with NoVA', 'Talk the idea through; the concept writes the purpose and the screens.');
    return step('lab', 'design', 'Say what the app is', 'A name and the outcome it must deliver — everything else reads from that.');
  }
  if (j && THINKING.has(j) && !s.concept?.accepted) {
    const cur = STUDIO_JOURNEY.find((x) => x.id === j);
    return step('lab', 'concept', cur?.id === 'review' ? 'Review and accept the concept' : 'Finish the concept with NoVA', cur?.goal || '');
  }
  // The design is behind what people said and built
  // ([[design-catches-up-from-the-build]]): catch up before anything else
  // reads the concept or the stories as the truth.
  if (s.behind) {
    const d = s.dev || {};
    const what = [d.devSessions ? `${d.devSessions} Dev Console session${d.devSessions === 1 ? '' : 's'}` : '', d.planeTurns ? `${d.planeTurns} turn${d.planeTurns === 1 ? '' : 's'} with NoVA` : ''].filter(Boolean).join(' and ') || 'recent work';
    return step('lab', 'concept', 'Catch up from the build', `${what} the design has not read — one press drafts the concept, ideas and stories from them.`, { catchup: true });
  }
  if (!s.agentCount) return step('lab', 'design', 'Attach an agent', 'Nobody can talk to this app until it has one.');
  if (!s.liveCount) return step('lab', 'design', 'Publish the documents it answers from', 'Drafts never answer; approve and publish at least one document.', { card: 'knowledge' });
  if (j === 'stories' && !(s.stories?.total > 0)) return step('lab', 'stories', 'Write the user stories', 'The build reads the accepted stories as its brief.');
  if (!s.linked) return step('lab', 'surfaces', 'Link or launch a hosted instance', 'The screens are built on the instance; pick one that is live, or launch one.');
  const inst = s.instance;
  if (inst && inst.status && inst.status !== 'live') return step('ship', null, `Wait for ${inst.slug || 'the instance'} to come up`, `It is ${inst.status}. The build starts once it is live.`);
  const b = s.build;
  if (!b?.state || b.state === 'blocked') return step('ship', null, 'Build on the instance', b?.state === 'blocked' ? `The last request was blocked: ${b.reason || 'see the card'}.` : 'Send the concept, stories and briefs to the Dev Console and start the first build.');
  if (b.state === 'requested' || b.state === 'underway') return step('ship', null, 'Build underway', `The Dev Console is building the screens${b.prUrl ? ' — a pull request is open' : ''}.`, { href: b.prUrl || null });
  // After the first build each accepted story is its own small build
  // (Stories board → "Build this"; the kit stamps built / shipped back).
  if (b.state === 'built' && s.stories?.unbuilt > 0 && !(s.stories.building > 0)) {
    const nx = s.stories.nextUnbuilt;
    return step('lab', 'stories', 'Build the next story', `${s.stories.unbuilt} accepted stor${s.stories.unbuilt === 1 ? 'y is' : 'ies are'} not built yet${nx?.title ? ` — next: "${nx.title}"` : ''}. "Build this" sends one to the Dev Console.`, { id: nx?.id || null });
  }
  if (!s.gates && s.pipelineRequirements?.unmet > 0) return step('lab', 'solution', 'Meet the workflow requirements', `${s.pipelineRequirements.unmet} requirement${s.pipelineRequirements.unmet === 1 ? ' is' : 's are'} not met on the linked workflow — the Solution board names them and the canvas shows the blocks.`);
  if (!s.gates) return step('ship', null, 'Clear the release gates', 'Workflow, integrations, QA and guardrails — the checklist names the open ones.');
  if (!['approved', 'released'].includes(s.release?.status)) return step('ship', null, 'Release', 'Every gate is green — approve the release in DevOps.');
  if (inst?.status === 'live') return step('operate', null, 'Watch it', 'People are using it. The next change starts in the Lab.');
  return step('ship', null, 'Release', 'Approve the release in DevOps.');
}

// `here` is the stage the page is on (or the stage a library page feeds).
// `signals` is the boolean map from signalsFrom. Neither implies the other.
// `gates` (from stageGates) marks later stages disabled with their blocker.
export function lifecycleState({ here = null, signals = null, gates = null } = {}) {
  const s = signals || {};
  const g = gates || {};
  return LIFECYCLE.map((stage) => ({
    id: stage.id,
    label: stage.label,
    purpose: stage.purpose,
    here: !!here && stage.id === here,
    reached: !!s[stage.id],
    blocker: g[stage.id] || null,
    disabled: !!g[stage.id] && !(here && stage.id === here),
  }));
}

// One block under the rail: the choice this stage is for, the facts already
// on the records, and the single next gap — which is `nextStep` when it
// lives on this stage, else a pointer to where it does live.
const DECISION = {
  lab: 'Shape what this app is, where it lives, and prove it works.',
  ship: 'Clear the gates, build it on the instance, then release.',
  operate: 'People are using it. Watch it, then change it.',
};

const clip = (s, n) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t ? t.slice(0, n) : '';
};

export function stageBrief({
  stage,
  app = null,
  concept = null,
  checklist = null,
  agent = null,
  kLive = 0,
  instance = null,
  stories = null,
  next = null,
  build = null,
} = {}) {
  const decision = DECISION[stage] || '';
  const decided = [];
  const add = (s) => { const t = clip(s, 180); if (t) decided.push(t); };
  const accepted = concept?.accepted || null;
  const live = Number(kLive) || 0;

  if (stage === 'lab') {
    add(app?.goal);
    add(app?.users ? `For ${app.users}` : '');
    if (accepted) add(`Concept v${accepted.version || 1} accepted`);
    if (agent?.name) add(agent.name);
    if (live) add(`${live} document${live === 1 ? '' : 's'} live`);
    if (instance?.slug) add(`${instance.slug} linked`);
    if (stories?.total) add(stories.verified ? `${stories.verified} of ${stories.total} stories verified` : `${stories.total} stories written`);
  } else if (stage === 'ship') {
    if (accepted) add(`Concept v${accepted.version || 1} accepted`);
    if (stories?.total) add(`${stories.accepted || stories.total} stories ready for the build`);
    if (build?.state) add(`First build ${build.state}`);
    if (stories?.built) add(`${stories.built} of ${stories.total} stories built on the instance`);
    if (checklist?.length) {
      const req = checklist.filter((c) => !c.optional);
      const ok = req.filter((c) => c.ok).length;
      if (ok) add(`${ok} of ${req.length} gates passed`);
    }
  } else if (stage === 'operate' && instance?.status === 'live' && instance.slug) {
    add(`${instance.slug} is live`);
  }

  const n = next || null;
  const labPhase = (id) => (LAB_PHASES.find((p) => p.id === id) || {}).label || id;
  let attention = null;
  if (n) {
    const where = n.stage === 'lab' ? `Lab › ${labPhase(n.phase)}` : (LIFECYCLE.find((x) => x.id === n.stage) || {}).label || n.stage;
    attention = n.stage === stage
      ? { text: n.text || n.label, tab: n.stage, extra: n.phase ? { phase: n.phase, card: n.card || null } : null, href: n.href || null, label: n.label }
      : { text: `Nothing is waiting here. Next: ${n.label} — ${where}.`, tab: n.stage, extra: n.phase ? { phase: n.phase, card: n.card || null } : null, href: n.href || null, label: n.label };
  }
  return { decision, decided, attention };
}
